import React, { useState } from 'react';

// 岩性类型
type Lithology = 'sandstone' | 'shale' | 'carbonate';

// 岩性转换系数
const LITHOLOGY_PARAMS: Record<Lithology, { a: number; b: number }> = {
  sandstone: { a: 1.08, b: -0.02 },
  shale: { a: 1.15, b: -0.04 },
  carbonate: { a: 1.05, b: -0.01 },
};

const LITHOLOGY_LABELS: Record<Lithology, string> = {
  sandstone: '砂岩',
  shale: '泥岩/页岩',
  carbonate: '碳酸盐岩',
};

export default function RockMechanics() {
  const [inputs, setInputs] = useState({
    // 基本参数
    depth: 1000,          // 埋深 (m)
    density: 2500,        // 密度 (kg/m³)
    lithology: 'sandstone' as Lithology, // 岩性

    // 测井参数
    ac: 60,              // 声波时差 AC (μs/ft)
    gr: 60,              // 伽马测井 GR (API)
    grClean: 30,         // 纯砂岩伽马值
    grShale: 120,        // 纯泥岩伽马值
    resistivity: 10,     // 电阻率 (Ω·m)
    rt: 20,              // 真电阻率
  });

  const [results, setResults] = useState<Record<string, number> | null>(null);

  const handleInputChange = (key: string, value: string | Lithology) => {
  if (key === 'lithology') {
    setInputs(prev => ({ ...prev, lithology: value as Lithology }));
  } else {
    const numValue = parseFloat(value as string) || 0;
    setInputs(prev => ({ ...prev, [key]: numValue }));
  }
};

  const handleCalculate = () => {
    const { depth, density, lithology, ac, gr, grClean, grShale, resistivity, rt } = inputs;
    const { a, b } = LITHOLOGY_PARAMS[lithology];

    const results: Record<string, number> = {};

    // ========== 第一步：计算波速 ==========
    // 纵波速度 Vp (km/s) = 1/AC × 1000 × 0.3048
    results.Vp = (1 / ac) * 1000 * 0.3048;

    // 横波速度 Vs (km/s) - 根据岩性选择公式
    if (lithology === 'shale') {
      // 泥岩: Vs = 0.862Vp - 1.172
      results.Vs = 0.862 * results.Vp - 1.172;
    } else {
      // 砂岩: Vs = Vp / sqrt(2.34 - 0.35Vp)
      results.Vs = results.Vp / Math.sqrt(2.34 - 0.35 * results.Vp);
    }

    // ========== 第二步：计算弹性参数 ==========
    // 弹性模量 Ed (GPa) = ρVp²(3Vp²-4Vs²)/(Vp²-Vs²) × 10^-6
    const vp2 = results.Vp * results.Vp;
    const vs2 = results.Vs * results.Vs;
    results.Ed = density * vp2 * (3 * vp2 - 4 * vs2) / (vp2 - vs2) * 1e-6;

    // 泊松比 μ = (Vp²-2Vs²)/(2(Vp²-Vs²))
    results.mu = (vp2 - 2 * vs2) / (2 * (vp2 - vs2));

    // 转换后的弹性模量 Es (GPa) = a × Ed
    results.Es = a * results.Ed;

    // 转换后的泊松比 μs = a × μ + b
    results.mu_s = a * results.mu + b;

    // 剪切模量 G (GPa) = ρVs² × 10^-6
    results.G = density * vs2 * 1e-6;

    // 体积模量 K (GPa) = ρ(Vp² - 4/3Vs²) × 10^-6
    results.K = density * (vp2 - (4 / 3) * vs2) * 1e-6;

    // ========== 第三步：计算强度参数 ==========
    // 内摩擦角 φ (°) = 60 - 0.08 × AC
    results.phi = Math.max(0, 60 - 0.08 * ac);
    const phiRad = (results.phi * Math.PI) / 180;
    const cosPhi = Math.cos(phiRad);
    const sinPhi = Math.sin(phiRad);

    // 内聚力 C (MPa) = 3.625 × 10^-3 × Sc × Es
    // 先计算抗压强度 Sc = 2Ccosφ/(1-sinφ)，这里需要迭代或简化
    // 简化处理：使用经验公式估算 C
    results.C = 3.625 * 1e-3 * results.Es * (1 - sinPhi) / (2 * cosPhi);

    // 抗压强度 Sc (MPa) = 2Ccosφ/(1-sinφ)
    results.Sc = (2 * results.C * cosPhi) / (1 - sinPhi);

    // 抗拉强度 St (MPa) = Ccosφ/(1+sinφ)
    results.St = (results.C * cosPhi) / (1 + sinPhi);

    // 摩尔库伦强度系数 M = 2sinφ/(1-sinφ)
    results.M = (2 * sinPhi) / (1 - sinPhi);

    // ========== 第四步：计算测井派生参数 ==========
    // 归一化自然伽马指数 I_GR = (GR - GR_clean) / (GR_shale - GR_clean)
    results.I_GR = (gr - grClean) / (grShale - grClean);

    // 泥质含量 Vsh = (2^(GCUR×I_GR) - 1) / (2^GCUR - 1)，GCUR通常取2
    const gcur = 2;
    results.Vsh = (Math.pow(2, gcur * results.I_GR) - 1) / (Math.pow(2, gcur) - 1);

    // 含水率 Sw = sqrt(n × a × Rw / (m × Rt))，假设 n=2, a=0.8, m=1.8
    const n = 2, a_factor = 0.8, m = 1.8, Rw = 0.01; // Rw=地层水电阻率
    results.Sw = Math.sqrt(n * a_factor * Rw / (m * rt));

    // ========== 第五步：计算地应力和井壁稳定 ==========
    // 垂直应力 σv (MPa) = ρ × g × h / 1e6
    results.sigmaV = density * 9.81 * depth / 1e6;

    // 水平应力估算（简化）
    results.sigmaH = results.sigmaV * (0.8 + 0.2 * results.mu_s);
    results.sigmah = results.sigmaV * (0.6 + 0.2 * results.mu_s);

    // 孔隙压力（简化）
    results.porePressure = results.sigmaV * 0.4;

    // 坍塌压力（莫尔-库伦）
    results.collapsePressure = results.Sc + (results.sigmah + results.sigmaH) / 2 -
      ((results.sigmaH - results.sigmah) / 2) * sinPhi;

    // 破裂压力（拉伸破坏）
    results.fracturePressure = results.porePressure + results.St +
      (3 * results.sigmah - results.sigmaH) / 2;

    // 安全泥浆密度窗口 (g/cm³)
    results.minMudDensity = results.collapsePressure * 1e6 / (9.81 * depth);
    results.maxMudDensity = results.fracturePressure * 1e6 / (9.81 * depth);

    setResults(results);
  };

  const handleReset = () => {
    setResults(null);
  };

  return (
    <div style={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <h2 style={{ marginBottom: '20px', color: '#333' }}>井壁稳定 — 岩石力学参数</h2>

      {/* 基本参数 */}
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '15px', color: '#333' }}>基本参数</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>埋深 (m)</label>
            <input
              type="number"
              value={inputs.depth}
              onChange={(e) => handleInputChange('depth', e.target.value)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>密度 (kg/m³)</label>
            <input
              type="number"
              value={inputs.density}
              onChange={(e) => handleInputChange('density', e.target.value)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>岩性</label>
            <select
              value={inputs.lithology}
              onChange={(e) => handleInputChange('lithology', e.target.value as Lithology)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              {Object.entries(LITHOLOGY_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* 测井参数 */}
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '15px', color: '#333' }}>测井参数</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px' }}>声波时差 AC (μs/ft)</label>
            <input
              type="number"
              value={inputs.ac}
              onChange={(e) => handleInputChange('ac', e.target.value)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px' }}>伽马测井 GR (API)</label>
            <input
              type="number"
              value={inputs.gr}
              onChange={(e) => handleInputChange('gr', e.target.value)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px' }}>纯砂岩GR (API)</label>
            <input
              type="number"
              value={inputs.grClean}
              onChange={(e) => handleInputChange('grClean', e.target.value)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px' }}>纯泥岩GR (API)</label>
            <input
              type="number"
              value={inputs.grShale}
              onChange={(e) => handleInputChange('grShale', e.target.value)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px' }}>电阻率 (Ω·m)</label>
            <input
              type="number"
              value={inputs.resistivity}
              onChange={(e) => handleInputChange('resistivity', e.target.value)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px' }}>真电阻率 Rt (Ω·m)</label>
            <input
              type="number"
              value={inputs.rt}
              onChange={(e) => handleInputChange('rt', e.target.value)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
        </div>
      </div>

      {/* 按钮区域 */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={handleCalculate}
          style={{
            padding: '10px 20px',
            backgroundColor: '#1890ff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '10px'
          }}
        >
          计算
        </button>
        <button
          onClick={handleReset}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3f9ff2',
            color: '#fcfdfd',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          重置结果
        </button>
      </div>

      {/* 计算结果 */}
      {results && (
        <div>
          {/* 波速参数 */}
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '15px', color: '#333' }}>波速参数</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#1890ff' }}>纵波速度 Vp</div>
                <div style={{ fontSize: '20px', marginTop: '5px' }}>{results.Vp.toFixed(3)} km/s</div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#1890ff' }}>横波速度 Vs</div>
                <div style={{ fontSize: '20px', marginTop: '5px' }}>{results.Vs.toFixed(3)} km/s</div>
              </div>
            </div>
          </div>

          {/* 弹性参数 */}
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '15px', color: '#333' }}>弹性参数</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#52c41a' }}>弹性模量 Ed</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{results.Ed.toFixed(3)} GPa</div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#52c41a' }}>泊松比 μ</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{results.mu.toFixed(4)}</div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#52c41a' }}>剪切模量 G</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{results.G.toFixed(3)} GPa</div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#52c41a' }}>体积模量 K</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{results.K.toFixed(3)} GPa</div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#52c41a' }}>转换模量 Es</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{results.Es.toFixed(3)} GPa</div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#52c41a' }}>转换泊松比 μs</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{results.mu_s.toFixed(4)}</div>
              </div>
            </div>
          </div>

          {/* 强度参数 */}
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '15px', color: '#333' }}>强度参数</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>内摩擦角 φ</div>
                <div style={{ fontSize: '20px', marginTop: '5px' }}>{results.phi.toFixed(2)} °</div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>内聚力 C</div>
                <div style={{ fontSize: '20px', marginTop: '5px' }}>{results.C.toFixed(3)} MPa</div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>抗压强度 Sc</div>
                <div style={{ fontSize: '20px', marginTop: '5px' }}>{results.Sc.toFixed(3)} MPa</div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>抗拉强度 St</div>
                <div style={{ fontSize: '20px', marginTop: '5px' }}>{results.St.toFixed(3)} MPa</div>
              </div>
            </div>
          </div>

          {/* 测井派生参数 */}
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '15px', color: '#333' }}>测井派生参数</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#722ed1' }}>GR指数 I_GR</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{results.I_GR.toFixed(3)}</div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#722ed1' }}>泥质含量 Vsh</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{(results.Vsh * 100).toFixed(1)}%</div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#722ed1' }}>含水率 Sw</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{(results.Sw * 100).toFixed(1)}%</div>
              </div>
            </div>
          </div>

          {/* 井壁稳定 */}
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginBottom: '15px', color: '#333' }}>井壁稳定分析</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '15px' }}>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#d46b08' }}>垂直应力 σv</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{results.sigmaV.toFixed(3)} MPa</div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#d46b08' }}>最大水平应力 σH</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{results.sigmaH.toFixed(3)} MPa</div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#d46b08' }}>最小水平应力 σh</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{results.sigmah.toFixed(3)} MPa</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '15px' }}>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#ff4d4f' }}>坍塌压力</div>
                <div style={{ fontSize: '20px', marginTop: '5px' }}>{results.collapsePressure.toFixed(3)} MPa</div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#52c41a' }}>破裂压力</div>
                <div style={{ fontSize: '20px', marginTop: '5px' }}>{results.fracturePressure.toFixed(3)} MPa</div>
              </div>
            </div>
            <div style={{ padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px', textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', color: '#1890ff', marginBottom: '10px' }}>安全泥浆密度窗口</div>
              <div style={{ fontSize: '24px' }}>
                <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>{results.minMudDensity.toFixed(3)}</span>
                {' ~ '}
                <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{results.maxMudDensity.toFixed(3)}</span>
                <span style={{ fontSize: '16px', marginLeft: '10px' }}>g/cm³</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 说明 */}
      <div style={{ marginTop: '20px', color: '#666', fontSize: '14px' }}>
        说明：当前计算使用测井数据反演岩石力学参数，包括波速计算、弹性参数、强度参数、测井派生参数和井壁稳定窗口。
      </div>
    </div>
  );
}